import React from "react";

import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import {
  addSaleLot,
  closeConfirmModal,
  deleteScanAction,
  openConfirmModal,
  setAnimalDeceased,
  uploadScansAction,
  uploadUnallocatedScans,
} from "actions";

import AnimalModal from "components/AnimalModal";

import { TRANSFER_STATUS } from "constants/nlis";
import { SaleRoundName } from "constants/sale";

import { neg1ToNulls, removeNulls } from "lib";

import {
  getBobbyCalfSaleLots,
  getNestedConsignments,
  getSaleRoundIdByName,
  getSaleLots,
  getSales,
  getScans,
  getUnsoldBobbyCalfSaleLots,
} from "selectors";

const AnimalModalContainer = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const params = useParams();
  const { EID, saleId } = params;

  const scans = useSelector(getScans);
  const allSaleLots = useSelector(getSaleLots);
  const allConsignments = useSelector(getNestedConsignments);
  const sales = useSelector(getSales);
  const saleRoundId = useSelector(state =>
    getSaleRoundIdByName(state, SaleRoundName.BobbyCalf),
  );
  const bobbyCalfSaleLots = useSelector(getBobbyCalfSaleLots);
  const unsoldBobbyCalfSaleLots = useSelector(getUnsoldBobbyCalfSaleLots);

  const scan = scans[EID];
  const selectedSale = sales[saleId];

  const selectedSaleLot =
    scan && scan.sale_lot_id
      ? bobbyCalfSaleLots.find(sl => sl.id === scan.sale_lot_id)
      : null;

  const animalIsSold = Boolean(
    selectedSaleLot &&
      selectedSaleLot.nlisSellFileStatus &&
      selectedSaleLot.nlisSellFileStatus !== TRANSFER_STATUS.ROLLED_BACK,
  );

  const selectableSaleLots = animalIsSold
    ? [selectedSaleLot]
    : unsoldBobbyCalfSaleLots;

  const uniqueByAuctionPen = (a, index, self) =>
    self.findIndex(b => a.auctionPenId === b.auctionPenId) === index;

  const auctionPens = selectableSaleLots.filter(uniqueByAuctionPen).map(sl => ({
    value: sl.auctionPenId,
    label: `${sl.buyerName} - ${sl.destinationProperty}`,
  }));

  const animalIsTaken = Boolean(
    selectedSaleLot &&
      selectedSaleLot.nlisTakeFileStatus &&
      selectedSaleLot.nlisTakeFileStatus !== TRANSFER_STATUS.ROLLED_BACK,
  );

  const selectableConsignments = animalIsTaken
    ? allConsignments.filter(c => c.id === selectedSaleLot.consignmentId)
    : allConsignments.filter(
        c =>
          !c.takeStatus ||
          c.takeStatus.filter(ts => ts.status !== TRANSFER_STATUS.ROLLED_BACK)
            .length === 0,
      );

  const consignments = selectableConsignments.map(c => ({
    value: c.id,
    label: c.vendorName,
  }));

  const deleteAnimal = () => {
    dispatch(
      openConfirmModal({
        title: "Are you sure?",
        message: `You are about to remove an animal with the EID ${scan.EID} from this sale day. Are you sure you want to remove this animal?`,
        actions: [
          {
            label: "No",
            secondary: true,
            onClick: () => {
              dispatch(closeConfirmModal());
            },
          },
          {
            label: "Yes",
            onClick: () => {
              dispatch(deleteScanAction(scan));
              dispatch(closeConfirmModal());
              history.goBack();
            },
          },
        ],
      }),
    );
  };

  const markDeceased = isDeceased => {
    dispatch(setAnimalDeceased(scan, isDeceased));
    history.goBack();
  };

  const saveAnimal = values => {
    if (
      scan.animal &&
      ((scan.animal.marked_deceased && !values.deceased) ||
        (!scan.animal.marked_deceased && values.deceased))
    ) {
      dispatch(setAnimalDeceased(scan, values.deceased));
    }

    if (values.consignment === null && values.auctionPen === null) {
      const uploadUnallocated = uploadUnallocatedScans;
      dispatch(
        uploadUnallocated(
          [{ EID: scan.EID, created: scan.created, remove_from_salelot: true }],
          selectedSale,
        ),
      );
    } else {
      const selectedConsignment = allConsignments.find(
        c => c.id === values.consignment,
      );
      if (selectedConsignment && selectedConsignment.saleLots) {
        const matchedSaleLot = selectedConsignment.saleLots.find(
          sl => sl.auctionPenId === values.auctionPen,
        );
        if (matchedSaleLot) {
          dispatch(uploadScansAction([scan], matchedSaleLot.id));
        } else {
          const saleLotId = uuidv4();
          const saleLotFromSamePen = Object.values(allSaleLots).find(
            sl => sl.auction_pen_id === values.auctionPen,
          );
          const saleLotPayload = {
            created: new Date().toUTCString(),
            id: saleLotId,
            pricing_type_id: selectedSale.pricing_type_id,
            auction_pen_id: values.auctionPen,
            buyer_id: saleLotFromSamePen.buyer_id,
            consignment_id: values.consignment,
            destination_property_id: saleLotFromSamePen.destination_property_id,
            livestocksale_id: selectedSale.livestocksale_id,
            quantity: 1,
            sale_round_id: saleRoundId,
          };
          dispatch(
            addSaleLot(removeNulls(neg1ToNulls(saleLotPayload)), saleLotId),
          );
          dispatch(uploadScansAction([scan], saleLotId));
        }
      }
    }

    history.goBack();
  };

  return (
    <AnimalModal
      closeSelf={() => history.goBack()}
      saveAnimal={saveAnimal}
      deleteAnimal={deleteAnimal}
      markDeceased={markDeceased}
      scan={scan}
      consignments={consignments}
      auctionPens={auctionPens}
      saleLots={allSaleLots}
      allowBlankConsignment={!animalIsTaken}
      allowBlankAuctionPen={!animalIsSold}
    />
  );
};

export default AnimalModalContainer;
